/*
 * dropdownBehavior.ts
 * General utility function that handles the behavior of the custom
 * dropdown and searchselect dropdowns. It takes care of the closing
 * of the dropdown when clicking next to it. It also ensures that the
 * dropdown a height of 90% of the available space and determines
 * whether the dropdown should be displayed at the top or bottom of
 * the screen. It also gives the dropdown a minimal width of 200px or
 * if the associated field is bigger gives it the width of the field.
 */

import { useEffect, MutableRefObject, useLayoutEffect } from "react";

// Close the dropdown list when clicking outside the dropdown
export const useClickOutside = (
    isOpen: boolean, 
    dropdownRef: MutableRefObject<HTMLElement | null>, 
    setIsOpen: (isOpen: boolean) => void
) => {
    useEffect(() => {
        const checkIfClickedOutside = (e: MouseEvent) => {
            if (isOpen && dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", checkIfClickedOutside);
        return () => {
            document.removeEventListener("mousedown", checkIfClickedOutside);
        }
    }, [isOpen, dropdownRef, setIsOpen]);
};

// Get the width of the field to apply to the dropdown
export const useSetDropdownWidth = (
    fieldRef: React.MutableRefObject<HTMLElement | null>,
    setDropdownWidth: (width: number) => void,
    fixedDropdownWidth?: number,
) => {
    useLayoutEffect(() => {
        if (!fieldRef.current) return;

        // Set a fixed width if given
        if (fixedDropdownWidth) {
            setDropdownWidth(fixedDropdownWidth);
            return;
        }

        // Observe any resize
        const observer = new ResizeObserver(() => {
            if (!fieldRef.current) return;

            // Get the width of the field element
            const width = fieldRef.current.offsetWidth;

            // Set the width, to a minimum of 160 px
            if (width > 0) {
                if (width < 160) {
                    setDropdownWidth(160);
                } else {
                    setDropdownWidth(width);
                }
            }
        });

        observer.observe(fieldRef.current);

        return () => observer.disconnect();
    }, [fixedDropdownWidth]);
};

// Calculate the dropdown position (above/below field) and max height on open
export const calculateDropdownPosition = (
	fieldRef: React.MutableRefObject<HTMLElement | null>,
	setDropdownAbove: (v: boolean) => void,
	setDropdownMaxHeight: (v: number) => void,
	dropdownHeight?: number
) => {
	if (!fieldRef.current) return;

	const fieldRect = fieldRef.current.getBoundingClientRect();
	const spaceAbove = fieldRect.top;
	const spaceBelow = window.innerHeight - fieldRect.bottom;

    // If a dropdown height is given
	if (dropdownHeight) {
        // If the dropdown doesn't fit below, set it above
		const showAbove = dropdownHeight > spaceBelow && dropdownHeight <= spaceAbove;

		setDropdownAbove(showAbove);
		return;
	}

	// If no dropdown height is given
	const showAbove = spaceAbove > spaceBelow;
	setDropdownAbove(showAbove);

    // Set the max height to be 90% of the available space
	const availableSpace = showAbove ? spaceAbove : spaceBelow;
	setDropdownMaxHeight(availableSpace * 0.9);
};